import React, { useState, useEffect } from 'react';
import { API_BASE_URL, API_URL } from "../config";
import { toast } from 'react-toastify';
import { 
  FaEnvelope, FaPaperPlane, FaFileAlt, 
  FaHistory, FaPlus, FaEdit, FaTrash,
  FaCopy, FaUsers, FaUserTie
} from 'react-icons/fa';
import AdminLayout from '../layouts/AdminLayout';

function EmailCenter({ onLogout }) {
  const [activeTab, setActiveTab] = useState('compose');
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [sentEmails, setSentEmails] = useState([]);
  const [clients, setClients] = useState([]);
  const [subbies, setSubbies] = useState([]);
  
  // Compose form
  const [compose, setCompose] = useState({
    to: '',
    subject: '',
    body: '',
    template: '',
    recipients: 'clients' // clients, subbies, all
  });

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchTemplates();
    fetchClients();
    fetchSubbies();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await fetch(`${API_URL}/email-templates`);
      const data = await response.json();
      if (data.success) {
        setTemplates(data.templates || []);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await fetch(`${API_URL}/clients`);
      const data = await response.json();
      if (data.success) {
        setClients(data.clients || []);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchSubbies = async () => {
    try {
      const response = await fetch(`${API_URL}/subcontractors`);
      const data = await response.json();
      if (data.success) {
        setSubbies(data.subcontractors || []);
      }
    } catch (error) {
      console.error('Error fetching subbies:', error);
    }
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // This will send emails to selected recipients
      const recipients = getRecipients();
      for (const recipient of recipients) {
        await fetch(`${API_URL}/test-send-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            to: recipient.email,
            subject: compose.subject,
            message: compose.body
          })
        });
      }
      toast(`Email sent to ${recipients.length} recipients!`);
      setCompose({ to: '', subject: '', body: '', template: '', recipients: 'clients' });
    } catch (error) {
      console.error('Error sending email:', error);
      toast('Failed to send email');
    } finally {
      setLoading(false);
    }
  };

  const getRecipients = () => {
    if (compose.recipients === 'clients') {
      return clients.map(c => ({ email: c.contact_email, name: c.company_name }));
    } else if (compose.recipients === 'subbies') {
      return subbies.map(s => ({ email: s.contact_email, name: s.company_name }));
    } else {
      return [...clients.map(c => ({ email: c.contact_email, name: c.company_name })), ...subbies.map(s => ({ email: s.contact_email, name: s.company_name }))];
    }
  };

  const loadTemplate = (template) => {
    setCompose({
      ...compose,
      subject: template.subject,
      body: template.body
    });
  };

  return (
    <AdminLayout activeTab="email" onLogout={onLogout} user={user}>
      <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-navy">📧 Email Center</h1>
          <p className="text-gray-500 text-sm">Send emails, manage templates, and track history</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 bg-white rounded-xl p-2 border border-gray-200">
        <button
          onClick={() => setActiveTab('compose')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'compose' ? 'bg-navy text-white' : 'text-gray-600 hover:bg-gray-100'}`}
        >
          <FaPaperPlane className="inline mr-2" /> Compose
        </button>
        <button
          onClick={() => setActiveTab('templates')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'templates' ? 'bg-navy text-white' : 'text-gray-600 hover:bg-gray-100'}`}
        >
          <FaFileAlt className="inline mr-2" /> Templates
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'history' ? 'bg-navy text-white' : 'text-gray-600 hover:bg-gray-100'}`}
        >
          <FaHistory className="inline mr-2" /> Sent History
        </button>
      </div>

      {/* Compose Tab */}
      {activeTab === 'compose' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <form onSubmit={handleSendEmail}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Recipients</label>
              <select
                value={compose.recipients}
                onChange={(e) => setCompose({ ...compose, recipients: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
              >
                <option value="clients">All Clients ({clients.length})</option>
                <option value="subbies">All Subcontractors ({subbies.length})</option>
                <option value="all">All Contacts ({clients.length + subbies.length})</option>
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Template</label>
              <select
                value={compose.template}
                onChange={(e) => {
                  const template = templates.find(t => t.id === e.target.value);
                  if (template) loadTemplate(template);
                  setCompose({ ...compose, template: e.target.value });
                }}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
              >
                <option value="">Select template...</option>
                {templates.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
              <input
                type="text"
                value={compose.subject}
                onChange={(e) => setCompose({ ...compose, subject: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                required
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
              <textarea
                value={compose.body}
                onChange={(e) => setCompose({ ...compose, body: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green min-h-[200px]"
                required
              />
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-navy text-white rounded-lg hover:bg-green hover:text-navy transition-colors disabled:opacity-50"
              >
                {loading ? 'Sending...' : <><FaPaperPlane className="inline mr-2" /> Send Email</>}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-navy">Email Templates</h3>
            <button className="flex items-center gap-2 px-3 py-1 bg-navy text-white rounded-lg text-sm hover:bg-green hover:text-navy transition-colors">
              <FaPlus size={12} /> New Template
            </button>
          </div>
          {templates.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No templates found</div>
          ) : (
            <div className="space-y-3">
              {templates.map((template) => (
                <div key={template.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-medium text-navy">{template.name}</h4>
                      <p className="text-xs text-gray-500 mt-1">{template.type}</p>
                      <p className="text-sm text-gray-600 mt-1 line-clamp-2">{template.subject}</p>
                    </div>
                    <div className="flex gap-2">
                      <button className="text-gray-400 hover:text-blue-500">
                        <FaEdit size={14} />
                      </button>
                      <button className="text-gray-400 hover:text-red-500">
                        <FaTrash size={14} />
                      </button>
                      <button className="text-gray-400 hover:text-green-500">
                        <FaCopy size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-navy mb-4">Sent History</h3>
          <div className="text-center py-12 text-gray-500">
            <div className="text-6xl mb-4">📨</div>
            <p>Email history will appear here once you start sending emails.</p>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default EmailCenter;
